import { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
    trackEvent,
    trackEcommerce,
    trackingConstants,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    type EcomProduct,
} from 'dibs-tracking';
import { getAbTestV2 } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { type RelayNode } from 'dibs-ts-utils/exports/RelayNode.ts';
import { PDP_SIMILAR_SOLD_ITEMS_MODULE_TEST_NAME } from '../../constants/abTestsConstants.ts';

import {
    type useSimilarSoldItemsTracking_viewer$key,
    type useSimilarSoldItemsTracking_viewer$data,
} from './__generated__/useSimilarSoldItemsTracking_viewer.graphql.ts';

const viewerFragment = graphql`
    fragment useSimilarSoldItemsTracking_viewer on Viewer {
        similarSoldItemsConnection(itemId: $itemId, first: 4, requestSourceRole: BUYER)
            @include(if: $afterDisplay) {
            edges {
                node {
                    itemId
                    singleItemSoldPrice
                    item {
                        browseUrl
                        categoryCode
                        ecommerceTrackingParams
                    }
                }
            }
        }
    }
`;

const { ECOM_PRODUCT_IMPRESSION, ECOM_PRODUCT_CLICK } = trackingConstants;
const { EVENT_VIEW_ITEM_LIST, EVENT_PRODUCT_INTERACTION, EVENT_SELECT_ITEM } = eventNameConstants;
const { STEP_INTERACTION_VIEW_ALL_CLICK, STEP_INTERACTION_SIMILAR_SOLD_ITEMS_MODULE_DISPLAYED } =
    stepInteractionConstants;
const { INTERACTION_TYPE_SIMILAR_SOLD_ITEMS, INTERACTION_TYPE_PDP } = interactionTypeConstants;
const SIMILAR_SOLD_ITEMS_LIST = 'pdp similar sold';

type SimilarSoldItemNode = NonNullable<
    RelayNode<NonNullable<useSimilarSoldItemsTracking_viewer$data['similarSoldItemsConnection']>>
>;

const getItemTrackingData = (similarItem: SimilarSoldItemNode, index: number): EcomProduct => {
    const { singleItemSoldPrice, item } = similarItem;
    const { browseUrl, categoryCode, ecommerceTrackingParams } = item || {};

    return {
        ...ecommerceTrackingParams,
        position: index + 1,
        categoryCode,
        category: browseUrl,
        dimension76: singleItemSoldPrice,
    };
};

const getRangeTrigger = (
    isBelowMinPriceEstimate: boolean,
    isBelowOrEqualToMaxPriceEstimate: boolean
): string => {
    if (isBelowMinPriceEstimate) {
        return 'below range';
    } else if (!isBelowOrEqualToMaxPriceEstimate) {
        return 'above range';
    }
    return 'within range';
};

export const useSimilarSoldItemsTracking = ({
    viewer: viewerRef,
}: {
    viewer: useSimilarSoldItemsTracking_viewer$key;
}): {
    trackEstimateValueBadgeClick: ({ belowEstimate }: { belowEstimate: boolean }) => void;
    trackSimilarSoldItemsModuleClick: () => void;
    trackEstimateValueBadgeImpression: ({ belowEstimate }: { belowEstimate: boolean }) => void;
    trackSimilarSoldQualifyingPdpLoad: ({
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
    }: {
        isBelowMinPriceEstimate: boolean;
        isBelowOrEqualToMaxPriceEstimate: boolean;
    }) => void;
    trackSimilarSoldItemsModuleVisible: ({
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
    }: {
        isBelowMinPriceEstimate: boolean;
        isBelowOrEqualToMaxPriceEstimate: boolean;
    }) => void;
    trackSimilarSoldItemsEcommerceClick: ({ itemId }: { itemId: string }) => void;
    trackSimilarSoldItemsEcommerceImpressions: () => void;
} => {
    const viewer = useFragment(viewerFragment, viewerRef);

    const similarSoldItems = useMemo(() => {
        return (
            viewer?.similarSoldItemsConnection?.edges
                ?.map(edge => edge?.node || null)
                ?.filter(filterNulls) ?? []
        );
    }, [viewer]);

    const trackSimilarSoldItemsModuleVisible = useCallback(
        ({
            isBelowMinPriceEstimate,
            isBelowOrEqualToMaxPriceEstimate,
        }: {
            isBelowMinPriceEstimate: boolean;
            isBelowOrEqualToMaxPriceEstimate: boolean;
        }) => {
            const abTest = getAbTestV2(PDP_SIMILAR_SOLD_ITEMS_MODULE_TEST_NAME);
            if (abTest?.variant) {
                trackEvent({
                    action: 'similar sold module displayed',
                    category: 'product',
                    label: 'pdp',
                    eventName: EVENT_PRODUCT_INTERACTION,
                    interaction_type: INTERACTION_TYPE_SIMILAR_SOLD_ITEMS,
                    step_interaction_name: STEP_INTERACTION_SIMILAR_SOLD_ITEMS_MODULE_DISPLAYED,
                    trigger: getRangeTrigger(
                        isBelowMinPriceEstimate,
                        isBelowOrEqualToMaxPriceEstimate
                    ),
                });
            }
        },
        []
    );

    const trackSimilarSoldQualifyingPdpLoad = useCallback(
        ({
            isBelowMinPriceEstimate,
            isBelowOrEqualToMaxPriceEstimate,
        }: {
            isBelowMinPriceEstimate: boolean;
            isBelowOrEqualToMaxPriceEstimate: boolean;
        }) => {
            trackEvent({
                eventName: EVENT_PRODUCT_INTERACTION,
                interaction_type: INTERACTION_TYPE_SIMILAR_SOLD_ITEMS,
                step_interaction_name: 'similar sold feature PDP',
                trigger: getRangeTrigger(isBelowMinPriceEstimate, isBelowOrEqualToMaxPriceEstimate),
            });
        },
        []
    );

    const trackSimilarSoldItemsModuleClick = useCallback(() => {
        trackEvent({
            action: 'similar sold module clicked',
            category: 'product',
            label: 'pdp',
            eventName: EVENT_PRODUCT_INTERACTION,
            interaction_type: INTERACTION_TYPE_SIMILAR_SOLD_ITEMS,
            step_interaction_name: STEP_INTERACTION_VIEW_ALL_CLICK,
            trigger: SIMILAR_SOLD_ITEMS_LIST,
        });
    }, []);

    const trackSimilarSoldItemsEcommerceImpressions = useCallback(() => {
        if (similarSoldItems.length > 0) {
            trackEcommerce({
                type: ECOM_PRODUCT_IMPRESSION,
                eventName: EVENT_VIEW_ITEM_LIST,
                trigger: SIMILAR_SOLD_ITEMS_LIST,
                products: similarSoldItems.map(getItemTrackingData),
            });
        }
    }, [similarSoldItems]);

    const trackSimilarSoldItemsEcommerceClick = useCallback(
        ({ itemId }: { itemId: string }) => {
            const similarItem = similarSoldItems.find(item => item?.itemId === itemId);
            if (similarItem) {
                trackEcommerce({
                    type: ECOM_PRODUCT_CLICK,
                    eventName: EVENT_SELECT_ITEM,
                    trigger: SIMILAR_SOLD_ITEMS_LIST,
                    products: [getItemTrackingData(similarItem, 0)],
                    actionField: { list: SIMILAR_SOLD_ITEMS_LIST, itemId },
                });
            }
        },
        [similarSoldItems]
    );

    const trackEstimateValueBadgeImpression = useCallback(
        ({ belowEstimate }: { belowEstimate: boolean }): void => {
            const abTest = getAbTestV2(PDP_SIMILAR_SOLD_ITEMS_MODULE_TEST_NAME);
            if (abTest?.variant) {
                trackEvent({
                    eventName: EVENT_PRODUCT_INTERACTION,
                    interaction_type: INTERACTION_TYPE_PDP,
                    step_interaction_name: 'pill displayed',
                    trigger: belowEstimate ? 'Well Priced' : 'Competitive Price',
                    isInteractiveEvent: true,
                });
            }
        },
        []
    );

    const trackEstimateValueBadgeClick = useCallback(
        ({ belowEstimate }: { belowEstimate: boolean }): void => {
            trackEvent({
                eventName: EVENT_PRODUCT_INTERACTION,
                interaction_type: INTERACTION_TYPE_PDP,
                step_interaction_name: 'pill clicked',
                trigger: belowEstimate ? 'Well Priced' : 'Competitive Price',
                isInteractiveEvent: true,
            });
        },
        []
    );

    return {
        trackEstimateValueBadgeClick,
        trackSimilarSoldItemsModuleClick,
        trackSimilarSoldQualifyingPdpLoad,
        trackEstimateValueBadgeImpression,
        trackSimilarSoldItemsModuleVisible,
        trackSimilarSoldItemsEcommerceClick,
        trackSimilarSoldItemsEcommerceImpressions,
    };
};
